import { Accordion, Flex, Grid, IconButton, Menu, Text, TrashIcon } from 'components'
import { uniq } from 'lodash-es'
import { Fragment, forwardRef, useEffect, useMemo, useState } from 'react'
import { helperLeadProperties, primaryLeadProperties } from 'types'
import { getDocById } from 'utils'
import { useEvent } from '../../../utils/eventUtils'
import PropertyMapper from '../../PropertyMapper'
import { MainPropertyMap, rootImportProperties, useSessionImport } from './SessionImportProvider'

const SessionImportMapping = forwardRef<HTMLDivElement>((_, ref) => {
  const { appType, data, setMainPropertyMap } = useSessionImport()

  const firstRowWithData = useMemo(() => (data ? getFirstRowWithData(data) : {}), [data])

  return appType === 'explorer' ? (
    <ExplorerMapping ref={ref} />
  ) : (
    <PropertyMapper
      _ref={ref}
      defaultProperties={['created']}
      inputMap={firstRowWithData}
      onChange={setMainPropertyMap}
      p={4}
      properties={mainProperties}
    />
  )
})

const ExplorerMapping = forwardRef<HTMLDivElement>((_, ref) => {
  const { data, setMainPropertyMap, explorerProperties, setExplorerProperties, momentId } =
    useSessionImport()
  const traitTitles = useTraitTitles(momentId || 0)

  const firstRowWithData = useMemo(() => (data ? getFirstRowWithData(data) : {}), [data])
  const numericData = useMemo(
    () =>
      Object.entries(firstRowWithData).reduce((acc, [property, value]) => {
        if (!isNaN(Number(value)) && value !== '') acc[property] = value
        return acc
      }, {} as Record<string, string>),
    [firstRowWithData]
  )

  return (
    <Accordion
      py={4}
      ref={ref}
      defaultValue={['0']}
      items={[
        {
          title: 'Primary Fields',
          content: (
            <PropertyMapper
              defaultProperties={['created']}
              hideCustom
              inputMap={firstRowWithData}
              onChange={setMainPropertyMap}
              properties={mainProperties}
              px={4}
            />
          ),
        },
        {
          title: 'Explorer Fields',
          content: (
            <Flex direction="column" gap={2} px={4}>
              <Text textStyle="bodySmall" opacity={0.7}>
                Traits
              </Text>
              <PropertyMapper
                properties={traitTitles}
                hideCustom
                inputMap={firstRowWithData}
                onChange={v =>
                  setExplorerProperties(s => ({ ...s, traits: Object.values(v || {}) }))
                }
              />

              <Text textStyle="bodySmall" mt={4} opacity={0.7}>
                Durations
              </Text>
              <PropertyMapper
                properties={['registration', 'traits', 'results']}
                hideCustom
                defaultProperties={['registration', 'traits', 'results']}
                inputMap={numericData}
                onChange={v =>
                  setExplorerProperties(s => ({ ...s, duration: { ...s.duration, ...v } }))
                }
              />

              <Text textStyle="bodySmall" mt={4} opacity={0.7}>
                Metrics
              </Text>
              <Grid templateColumns="1fr 1fr 40px" gap={2}>
                {explorerProperties.metrics.map(property => (
                  <Fragment key={property}>
                    <Text {...textProps}>{property}</Text>
                    <Text {...textProps}>{firstRowWithData?.[property]}</Text>
                    <IconButton
                      icon={<TrashIcon />}
                      onClick={() =>
                        setExplorerProperties(s => ({
                          ...s,
                          metrics: s.metrics.filter(p => p !== property),
                        }))
                      }
                      aria-label="Remove metric"
                    />
                  </Fragment>
                ))}
              </Grid>
              <Menu
                alignSelf="flex-start"
                variant="outline"
                hideCheckbox
                options={Object.keys(numericData).map(value => ({ value }))}
                onChange={v => setExplorerProperties(s => ({ ...s, metrics: [...s.metrics, v] }))}
                buttonLabel="Add Metric"
              />
            </Flex>
          ),
        },
      ]}
    />
  )
})

const mainProperties: (keyof MainPropertyMap)[] = [
  ...rootImportProperties,
  ...primaryLeadProperties,
  ...helperLeadProperties,
]

const textProps = {
  bg: 'rgba(23, 23, 23, 0.10)',
  borderRadius: '10px',
  minW: '100px',
  h: '40px',
  py: '10px',
  px: '16px',
  textStyle: 'bodyMedium',
  fontWeight: '500',
  truncate: true,
}

function getFirstRowWithData(data: Record<string, any>[]) {
  let res: Record<string, any> = {}
  for (const row of data) {
    for (const key in row) {
      if (!res[key]) res[key] = row[key]
    }
  }
  return res
}

function useTraitTitles(momentId: number) {
  const { event } = useEvent()
  const [titles, setTitles] = useState<string[]>([])

  useEffect(() => {
    ;(async () => {
      const templateId = event?.moments?.find(m => m.id === momentId)?.template.id
      const template = templateId ? await getDocById('templates', templateId) : undefined
      if (!template?.traitMap) return

      const groups = await Promise.all(
        Object.values(template.traitMap).map(async g => {
          let title = g.title?.en
          const group = g.id ? await getDocById('traits2', g.id) : undefined
          if (!title) title = group?.title?.en

          return title
        })
      )

      setTitles(uniq(groups).filter(Boolean) as string[])
    })()
  }, [event, momentId])

  return titles
}

export default SessionImportMapping
